import { Location, LocationStrategy, NgClass, DatePipe } from '@angular/common';
import { Component, OnDestroy, AfterViewInit, computed } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { LazyLoadEvent } from 'primeng/api';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { ModelListBaseComponent } from '../shared/components/model-list-base.component';
import { AppSettings } from '../shared/models/appsettings';
import { EventsService } from '../shared/services/events.service';
import { UserContextService } from '../shared/services/user-context.service';
import { AvatarCacheService } from '../shared/services/avatar-cache.service';
import { ActivityEventsService } from './shared/activity-events.service';
import { IActivityEventQuery } from './shared/iactivity-event-query.model';
import { IActivityEvent, IActivityEventType } from './shared/iactivity-event.model';
import { UsersService } from '../users/shared/users.service';
import { IIdeaStatus } from '../ideastatuses/shared/iidea-status.model';
import {
  WelcomeActivityFeedFilterService,
  WelcomeFeedFilterState,
} from '../info/welcome-activity-feed-filter.service';
import { hexToRgba, toCssHexColor } from '../shared/utils/css-color.util';
import { IIdeaStat } from '../ideas/shared/idea-stat.model';
import { ReferenceDataService } from '../shared/services/reference-data.service';
import { UiTabsComponent } from '../shared/components/ui-tabs/ui-tabs.component';
import { LoadingUiBlockerComponent } from '../shared/components/loading-ui-blocker/loading-ui-blocker.component';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-activity-events',
  templateUrl: './activity-events.component.html',
  styleUrls: ['./activity-events.component.scss'],
  imports: [
    UiTabsComponent,
    LoadingUiBlockerComponent,
    RouterModule,
    NgClass,
    TooltipModule,
    DatePipe,
  ],
  providers: [AvatarCacheService],
})
export class ActivityEventsComponent
  extends ModelListBaseComponent<number, IActivityEvent>
  implements OnDestroy, AfterViewInit
{
  public imgNewIdea = new URL('../../assets/img/new_idea.png', import.meta.url);
  public onlyNewIdeas = false;
  public onlyNewComments = false;
  public isMobileLayout = false;
  public activeTab: 'all' | 'ideas' | 'comments' | 'decisions' = 'all';

  /** Фильтр ленты по клику в статистике welcome (IdeaStatusId из категории группировки). */
  feedFilterState: WelcomeFeedFilterState = {
    groupingCategoryId: null,
    categoryLabel: null,
  };

  private readonly destroy$ = new Subject<void>();
  private _dateObservers: IntersectionObserver[] = [];

  constructor(
    protected events: EventsService,
    protected modelSvc: ActivityEventsService,
    protected usersSvc: UsersService,
    protected userContextSvc: UserContextService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected location: Location,
    protected locationStrategy: LocationStrategy,
    private welcomeFeedFilter: WelcomeActivityFeedFilterService,
    private avatarCacheService: AvatarCacheService,
    private referenceDataSvc: ReferenceDataService
  ) {
    super(events, modelSvc, userContextSvc, router, route, location, locationStrategy);
  }

  ngOnInit() {
    super.ngOnInit();
    this.rowsPerPage = AppSettings.activityEventsRowsPerPage;
    this.appendLoadedEntities = true;
    this.feedFilterState = this.welcomeFeedFilter.getSnapshot();

    if (!this.userContextSvc.currentUser.GlobalAdmin)
      this.isEditable = false;
    const lazyLoadEvent: LazyLoadEvent = { first: this.pageOffset, rows: this.rowsPerPage };
    this.onLazyLoad(lazyLoadEvent);

    this.welcomeFeedFilter.state
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe((state) => {
        this.feedFilterState = state;
        this.entities = [];
        this.resetPagingParams();
        this.onLazyLoad({ first: 0, rows: this.rowsPerPage });
      });

    if (window.screen.width <= AppSettings.screenSettings.width) {
      this.isMobileLayout = true;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    try {
      this._dateObservers.forEach((o) => o.disconnect());
    } catch (e) {
      // ignore
    }
  }

  private setupDateStickyObservers(): void {
    if (typeof window === 'undefined' || !document) return;
    const feedWrapper = document.querySelector('.hm-feed-content');
    if (!feedWrapper) return;

    const wrappers = Array.from(feedWrapper.querySelectorAll<HTMLElement>('.hm-date__wrapper'));

    if (!wrappers.length) return;

    wrappers.forEach((wrapper) => {
      const groupRows: HTMLElement[] = [];
      let el = wrapper.nextElementSibling as HTMLElement | null;
      while (el && !el.classList.contains('hm-date__wrapper')) {
        if (el.classList.contains('hm-feed-row')) {
          groupRows.push(el);
        }
        el = el.nextElementSibling as HTMLElement | null;
      }

      if (!groupRows.length) return;

      const visible = new Set<HTMLElement>();
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          if (entry.isIntersecting) visible.add(target);
          else visible.delete(target);
        });
        if (visible.size > 0) wrapper.classList.add('is-sticky');
        else wrapper.classList.remove('is-sticky');
      }, { root: null, threshold: 0.1 });

      groupRows.forEach((r) => observer.observe(r));
      this._dateObservers.push(observer);
    });
  }

  clearWelcomeFeedFilter(): void {
    this.welcomeFeedFilter.clear();
  }

  onLazyLoad(event: LazyLoadEvent) {
    let query: IActivityEventQuery = {}
    query.Skip = event ? event.first : 0;
    query.Take = event ? event.rows : this.rowsPerPage;
    query.BusinessFunctionIds = this.userContextSvc.currentUser.SelectedBF ? [this.userContextSvc.currentUser.SelectedBF.Id] : [];
    query.ExcludeActivityEventTypesIds = [AppSettings.actionEvent.eventType.ideaTaskDone];
    query.Sorts = [{ Field: "Id", Desc: true }];

    if (this.activeTab === 'decisions') {
      query.ExcludeActivityEventTypesIds.push(
        AppSettings.actionEvent.eventType.ideaRegisteredAsNew,
        AppSettings.actionEvent.eventType.ideaCommented
      );
    }

    if (this.onlyNewIdeas)
      query.ActivityEventTypesIds = [AppSettings.actionEvent.eventType.ideaRegisteredAsNew];
    if (this.onlyNewComments)
      query.ActivityEventTypesIds = [AppSettings.actionEvent.eventType.ideaCommented];
    if (this.feedFilterState.groupingCategoryId != null) {
      query.GroupingCategoryIds = [this.feedFilterState.groupingCategoryId];
    }
    this.loadPagedPromise(query)
      .then(() => {
        this.loading = false
        requestAnimationFrame(() => this.setupDateStickyObservers());
      })
      .catch(() => { });
  }

  onOnlyNewIdeasChange(isChecked: boolean) {
    if (isChecked)
      this.onlyNewComments = false;
    this.entities = [];
    this.onLazyLoad(null);
  }

  onOnlyNewCommentsChange(isChecked: boolean) {
    if (isChecked)
      this.onlyNewIdeas = false;
    this.entities = [];
    this.onLazyLoad(null);
  }

  public onSelectTab(tab: 'all' | 'ideas' | 'comments' | 'decisions') {
    this.activeTab = tab;
    this.onlyNewIdeas = tab === 'ideas';
    this.onlyNewComments = tab === 'comments';
    this.entities = [];
    this.onLazyLoad(null);
  }

  public isNewIdeaEvent(row: IActivityEvent): boolean {
    return row.ActivityEventTypeId === AppSettings.actionEvent.eventType.ideaRegisteredAsNew;
  }

  public isCommentEvent(row: IActivityEvent): boolean {
    return row.ActivityEventTypeId === AppSettings.actionEvent.eventType.ideaCommented;
  }

  public isCardEvent(row: IActivityEvent): boolean {
    return this.isNewIdeaEvent(row) || this.isCommentEvent(row);
  }

  /** Лайки идеи для карточки ленты (данные из Idea.Stat вместе с событием). */
  public getIdeaStatLikes(row: IActivityEvent): number {
    const st = this.getIdeaStatFromRow(row);
    const v = st?.LikesCount ?? st?.likesCount;
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  }

  public getIdeaStatViews(row: IActivityEvent): number {
    const st = this.getIdeaStatFromRow(row);
    const v = st?.ViewsCount ?? st?.viewsCount;
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  }

  private getIdeaStatFromRow(row: IActivityEvent): {
    LikesCount?: number;
    likesCount?: number;
    ViewsCount?: number;
    viewsCount?: number;
  } | undefined {
    const idea = row.Idea as
      | (typeof row.Idea & {
        Stat?: { LikesCount?: number; ViewsCount?: number };
        stat?: { likesCount?: number; viewsCount?: number };
      })
      | undefined;
    if (!idea) {
      return undefined;
    }
    return (idea.Stat ?? idea.stat) as {
      LikesCount?: number;
      likesCount?: number;
      ViewsCount?: number;
      viewsCount?: number;
    };
  }

  /**
   * Статус идеи только из row.Idea (API всегда отдаёт Idea со статусом).
   * Если данных нет — технический статус (нейтральное отображение).
   */
  public getResolvedIdeaStatusId(row: IActivityEvent): number {
    const s = AppSettings.idea.status;
    const explicit = row.Idea?.StatusId;
    if (explicit != null && explicit > 0) {
      return explicit;
    }
    return s.technicalId;
  }

  private resolveIdeaStatus(row: IActivityEvent): IIdeaStatus | null {
    const id = this.getResolvedIdeaStatusId(row);
    const bf = this.userContextSvc.currentUser.SelectedBF;
    const link = bf?.BFIdeaStatuses?.find((x) => {
      const lid = x.IdeaStatusId ?? (x as { ideaStatusId?: number }).ideaStatusId;
      const stid = x.IdeaStatus?.Id ?? (x.IdeaStatus as { id?: number } | undefined)?.id;
      return lid === id || stid === id;
    });
    return (
      link?.IdeaStatus ??
      (link as { ideaStatus?: IIdeaStatus } | undefined)?.ideaStatus ??
      null
    );
  }

  /** Цвет и иконка типа события (ActivityEventTypes на бэке). */
  public getEventTypeDisplayColor(row: IActivityEvent): string | undefined {
    const ae = this.getActivityEventType(row);
    const color = ae?.DisplayColor;
    if (!color) return undefined;

    return toCssHexColor(color) ?? color.trim();
  }

  public getEventTypeIconClass(row: IActivityEvent): string {
    const ae = this.getActivityEventType(row);
    const iconClass = ae?.IconClass?.trim();

    return iconClass ? iconClass : 'fas fa-bell';
  }

  public eventTypeChipBackground(row: IActivityEvent): string | null {
    const c = this.getEventTypeDisplayColor(row);
    return c ? hexToRgba(c, 0.18) : null;
  }

  public eventTypeChipColor(row: IActivityEvent): string | null {
    return this.getEventTypeDisplayColor(row) ?? null;
  }

  public getCardStatusLabel(row: IActivityEvent): string {
    const meta = this.resolveIdeaStatus(row);
    if (meta?.Title?.trim()) {
      return meta.Title.trim();
    }
    const statusId = this.getResolvedIdeaStatusId(row);
    return (
      AppSettings.idea.status.list.find((x) => x.value === statusId)?.label ||
      'Статус идеи'
    );
  }

  public getTimelineDotClass(row: IActivityEvent): string {
    return this.getEventTypeDisplayColor(row) ? '' : 'is-gray';
  }

  /** Резерв под иконку в карточке, если нет своего цвета с бэка. */
  public getEventTypeStatToneClass(row: IActivityEvent): string {
    return this.getEventTypeDisplayColor(row) ? '' : 'is-gray';
  }

  public showDayHeader(currentIndex: number): boolean {
    if (currentIndex === 0) {
      return true;
    }
    const prevLabel = this.getDayLabel(this.entities[currentIndex - 1]?.Created);
    const currentLabel = this.getDayLabel(this.entities[currentIndex]?.Created);
    return prevLabel !== currentLabel;
  }

  public getDayLabel(dateValue: Date | string): string {
    if (!dateValue) {
      return '';
    }
    const eventDate = new Date(dateValue);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();

    if (isSameDay(eventDate, today)) {
      return 'Сегодня';
    }
    if (isSameDay(eventDate, yesterday)) {
      return 'Вчера';
    }
    return eventDate.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
    });
  }

  private getUserInfo(row: IActivityEvent): { id: number | null; email: string | null } {
    const id = row?.CreatedBy?.Id ?? row?.CreatedById ?? null;
    const email = row?.CreatedBy?.Email ?? row?.CreatedBy?.PersonalEmail ?? null;
    return { id, email };
  }

  public getUserAvatarUrl(row: IActivityEvent): string | null {
    const { id: userId, email } = this.getUserInfo(row);
    return this.avatarCacheService.getAvatarUrl(userId, email);
  }

  public getEventDescription(row: IActivityEvent, includeIdeaLink: boolean = true): string {
    const baseUrl = '#/idea-v2/';
    const ideaLink = '<a href="' + baseUrl + row.IdeaId + '">идею #' + row.IdeaId + '</a>';
    const ideaLinkCapitalized = '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a>';
    switch (row.ActivityEventTypeId) {

      case AppSettings.actionEvent.eventType.ideaSaveAsDraft:
        return 'Сохранен черновик <a href="' + baseUrl + row.IdeaId + '">Идеи #' + row.IdeaId + '</a>.';
      case AppSettings.actionEvent.eventType.ideaRegisteredAsNew:
        return includeIdeaLink
          ? 'предложил/а новую ' + ideaLink + '.'
          : 'предложил/а новую идею.';
      case AppSettings.actionEvent.eventType.ideaSendToExpertApproval:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на валидацию экспертов.';
      case AppSettings.actionEvent.eventType.ideaExpertApproved:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была валидирована экспертами.';
      case AppSettings.actionEvent.eventType.ideaExpertRejected:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена валидаторами.';
      case AppSettings.actionEvent.eventType.ideaMarkedAsNotIdea:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> не была признана идеей.';
      case AppSettings.actionEvent.eventType.ideaSendToDirectionOUExpertApproval:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на согласование экспертами направления оргединицы.';
      case AppSettings.actionEvent.eventType.ideaDirectionOUExpertApproved:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была утверждена экспертами направления оргединицы.';
      case AppSettings.actionEvent.eventType.ideaDirectionOUExpertRejected:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена экспертами направления оргединицы.';
      case AppSettings.actionEvent.eventType.ideaSendToOUExpertCouncilApproval:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на согласование экспертного совета оргединицы.';
      case AppSettings.actionEvent.eventType.ideaOUExpertCouncilApproved:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была согласована экспертным советом оргединицы.';
      case AppSettings.actionEvent.eventType.ideaOUExpertCouncilRejected:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена экспертным советом оргединицы.';
      case AppSettings.actionEvent.eventType.ideaCommented:
        return includeIdeaLink
          ? 'прокомментировал/а ' + ideaLink + '.'
          : 'прокомментировал/а идею.';
      case AppSettings.actionEvent.eventType.ideaOUECDecisionRejected:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена решением экспертного совета.';
      case AppSettings.actionEvent.eventType.ideaOUECDecisionPilot:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена решением экспертного совета на пилотную реализацию в оргединицу.';
      case AppSettings.actionEvent.eventType.ideaOUECDecisionTC5Recommended:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была рекомендована экспертным советом к реализации в ТС5.';
      case AppSettings.actionEvent.eventType.ideaOURealizationPlanAttached:
        return 'Добавлен план реализации пилота в оргединице для <a href="' + baseUrl + row.IdeaId + '">Идеи #' + row.IdeaId + '</a>.';
      case AppSettings.actionEvent.eventType.ideaOURealizationCompleted:
        return 'Был завершен пилот <a href="' + baseUrl + row.IdeaId + '">Идеи #' + row.IdeaId + '</a> в оргединице.';
      case AppSettings.actionEvent.eventType.ideaCOECDecisionPilot:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена решением экспертного совета ЦО на пилотную реализацию в ТС5.';
      case AppSettings.actionEvent.eventType.ideaCORealizationPlanAttached:
        return 'Добавлен план реализации пилота в ТС5 для <a href="' + baseUrl + row.IdeaId + '">Идеи #' + row.IdeaId + '</a>.';
      case AppSettings.actionEvent.eventType.ideaCORealizationCompleted:
        return 'Был завершен пилот <a href="' + baseUrl + row.IdeaId + '">Идеи #' + row.IdeaId + '</a> в ТС5.';
      case AppSettings.actionEvent.eventType.ideaCOECDecisionReturnForOUPilot:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была возвращена решением экспертного совета ЦО на подготовку для пилотной реализации в оргединице.';
      case AppSettings.actionEvent.eventType.ideaCOECDecisionReturnForRevision:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была возвращена решением экспертного совета ЦО на доработку в оргединицу.';
      case AppSettings.actionEvent.eventType.ideaSendToDirectionExpertApproval:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на согласование экспертами направления.';
      case AppSettings.actionEvent.eventType.ideaDirectionExpertApproved:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была согласована экспертами направления.';
      case AppSettings.actionEvent.eventType.ideaDirectionExpertRejected:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена экспертами направления.';
      case AppSettings.actionEvent.eventType.ideaSendToDirectionExpertCOApproval:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на согласование экспертом направления ЦО.';
      case AppSettings.actionEvent.eventType.ideaDirectionExpertCOApproved:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была согласована экспертом направления ЦО.';
      case AppSettings.actionEvent.eventType.ideaDirectionExpertCORejected:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена экспертом направления ЦО.';
      case AppSettings.actionEvent.eventType.ideaNotRelevant:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была признана неактуальной экспертным советом ЦО.';
      case AppSettings.actionEvent.eventType.ideaWaitPilotFileOU:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> ожидает загрузки плана реализации пилота в оргединице.';
      case AppSettings.actionEvent.eventType.ideaWaitPilotFileCO:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> ожидает загрузки плана реализации пилота в ЦО.';
      case AppSettings.actionEvent.eventType.ideaWaitPilotResultOU:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> ожидает подтверждения реализации плана пилота в оргединице.';
      case AppSettings.actionEvent.eventType.ideaWaitPilotResultCO:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> ожидает подтверждения реализации плана пилота в ЦО.';
      case AppSettings.actionEvent.eventType.ideaSendExpertDVRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на согласование эксперту ДВРТ.';
      case AppSettings.actionEvent.eventType.ideaIsOkExpertDVRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была согласована экспертом ДВРТ.';
      case AppSettings.actionEvent.eventType.ideaSendDoWorkExpertDVRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на доработку эксперту ДВРТ.';
      case AppSettings.actionEvent.eventType.ideaNotIdea:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> не была признана идеей.';
      case AppSettings.actionEvent.eventType.ideaSendExpertDRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на согласование ответственному от ДРТ.';
      case AppSettings.actionEvent.eventType.ideaCancelExpertDRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была откланена ответственным от ДРТ.';
      case AppSettings.actionEvent.eventType.ideaIsOkExpertDRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была согласована ответственным от ДРТ.';
      case AppSettings.actionEvent.eventType.ideaSendToRealizationOU:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была передана на реализацию в ОЕ.';
      case AppSettings.actionEvent.eventType.ideaSendToRealizationCO:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была передана на реализацию в ЦО.';
      case AppSettings.actionEvent.eventType.ideaDoWorkExpertDVRT:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отправлена на доработку к эксперту ДВРТ.';
      case AppSettings.actionEvent.eventType.ideaIsOkForPilotCO:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была принята к пилоту реализации в ЦО ТС5.';
      case AppSettings.actionEvent.eventType.ideaCancelDVRTExpert:
        return '<a href="' + baseUrl + row.IdeaId + '">Идея #' + row.IdeaId + '</a> была отклонена бизнес-экспертом ДВРТ.';
      default:
        return (
          'Для ' +
          ideaLinkCapitalized +
          ' произошло событие: ' +
          this.getActivityEventType(row)?.Title
        );
    }
  }

  public getEventDescriptionNoDot(row: IActivityEvent, includeIdeaLink: boolean = true): string {
    return this.getEventDescription(row, includeIdeaLink).replace(/\.\s*$/, '');
  }

  public getEmptyStateMessage(): string {
    if (this.feedFilterState.categoryLabel) {
      return 'Нет событий для выбранной группы статусов. Попробуйте «Показать ещё» или снимите фильтр.';
    }
    switch (this.activeTab) {
      case 'ideas':
        return 'В этом разделе пока нет событий по новым идеям.';
      case 'comments':
        return 'В этом разделе пока нет событий по комментариям.';
      case 'decisions':
        return 'В этом разделе пока нет событий по решениям.';
      default:
        return 'События пока не найдены.';
    }
  }

  /** TODO - не самый оптимальный вариант. Определяет тон события для dot классов (accent, bad, good) */
  public getEventTone(row: IActivityEvent): string {
    const color = this.getEventTypeDisplayColor(row);
    if (!color) return 'accent';

    if (color.toLowerCase() === '#e24d4d' || color.toLowerCase().includes('e24d4d')) {
      return 'bad';
    }
    if (color.toLowerCase() === '#12a772' || color.toLowerCase().includes('12a772')) {
      return 'good';
    }
    return 'accent';
  }

  public getEventAction(row: IActivityEvent): string {
    if (this.isNewIdeaEvent(row)) {
      return 'предложил/а новую идею';
    }
    if (this.isCommentEvent(row)) {
      return 'прокомментировал/а';
    }
    return 'обновил/а';
  }

  /** Имя создателя события */
  public getEventCreatorName(row: IActivityEvent): string {
    return row.CreatedBy?.Title || 'Неизвестный пользователь';
  }

  /** Должность создателя события */
  public getEventCreatorRole(row: IActivityEvent): string {
    return row.CreatedBy?.JobTitle || 'Не указана';
  }

  public getEventCreatorId(row: IActivityEvent): number | undefined {
    return row.CreatedById;
  }

  /** Название типа события для отображения (статус) */
  public getEventTypeDisplayLabel(row: IActivityEvent): string {
    const meta = this.resolveIdeaStatus(row);
    if (meta?.Title?.trim()) {
      return meta.Title.trim();
    }

    return this.getActivityEventType(row)?.Title || 'Событие';
  }

  /** Количество комментариев для идеи */
  public getIdeaStatComments(row: IActivityEvent): number {
    const st = this.getIdeaStatFromRow(row);
    const v = (st as IIdeaStat)?.CommentsCount;
    return typeof v === 'number' && Number.isFinite(v) ? v : 0;
  }

  getIdeaStatus(row: IActivityEvent) {
    let statusId = row.Idea?.StatusId;
    if (!statusId) statusId = AppSettings.idea.status.technicalId;

    return this.referenceDataSvc.getStatus(statusId);
  }

  getActivityEventType(row: IActivityEvent): IActivityEventType | undefined {
    if (!row.ActivityEventTypeId) return undefined;
    return this.referenceDataSvc.getActivityEvent(row.ActivityEventTypeId);
  }
}
